/*
 * Search diagnostics panel - dev / test only (QA doc docs features/website/search-diagnostics/README.md R-SD2 .. R-SD6, R-SD9, website-web#47).
 * Shows the Trace ID (copyable) and one row per provider the search asked: name, kind, answer time, outcome (+ error code), offers found.
 * It renders ONLY when the API sent a `diagnostics` block (online-api's gate is open on dev / test); the page never decides by host or build,
 * and there is no placeholder when the block is absent. Open by default, remembered per browser. Laptop: a four-column table; narrower
 * than 560 px of the panel's own width the rows reflow into cards (container query - C12). Texts through the translation files (C21).
 * mode="summary" (run 24, app-search-supplier-summary): the same rows as the end-of-search supplier summary - seconds, answered / no answer /
 * error, the slowest answer highlighted; a supplier's time reads like the time chip of its rows ("in 2 s 297 ms", R-SD15). Tours (QA doc processes/tour-search-and-checkout-totals.md R-NR1, R-NR3): a provider the search never asked shows
 * "Not asked" with its reason code (outcome `skipped`, e.g. NO_AIRPORT_DISTRICT), and the flights left out are listed per airport under the table.
 * R-SD17 (docs#402): a supplier the search DID ask but cut when it ended with a refusal shows "Not needed" with the reason (outcome `not-needed`, e.g.
 * NO_FLIGHTS) - never "Not asked"; it is not counted as answered.
 * Kazunion Website Flags R-WF32: while the agent's last flags read says `website-supplier-answers` is off, the box closes on the open page
 * (no new search needed); the next search's answer no longer carries the block at all.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { DroppedFlightsDiagnostic, ProviderDiagnostic, SearchDiagnostics } from '@appTypes/api.types';
import { AviaTraceIdComponent } from '@pages/avia-page/components/avia-trace-id/avia-trace-id.component';
import { TranslatePipe } from '@ngx-translate/core';
import { WebsiteFlags } from '@core/services/website-flags.service';
import { ResponseTimePartsPipe } from '@shared/pipes/response-time-parts.pipe';

const OPEN_STORAGE_KEY = 'searchDiagnosticsOpen';

const readOpen = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(OPEN_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

const writeOpen = (open: boolean): void => {
  try {
    globalThis.localStorage?.setItem(OPEN_STORAGE_KEY, String(open));
  } catch {
    /* a private window or blocked storage: the choice just is not remembered */
  }
};

@Component({
  selector: 'app-search-diagnostics-panel',
  imports: [TranslatePipe, AviaTraceIdComponent, ResponseTimePartsPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (shownDiagnostics(); as d) {
      <section
        class="sd-panel"
        [class.sd-summary]="summary()"
        [attr.data-testid]="summary() ? 'search-supplier-summary' : 'search-diagnostics-panel'"
        [attr.aria-label]="title() | translate"
      >
        <header class="sd-head">
          <b>{{ title() | translate }}</b>
          @if (summary()) {
            <span class="sd-counts" data-testid="search-supplier-summary-counts">{{
              'Answered {answered} of {total}' | translate: { answered: answeredCount(), total: rows().length }
            }}</span>
          }
          @if (d.traceId || !summary()) {
            <app-avia-trace-id testIdPrefix="search-diagnostics" [activityId]="d.traceId" />
          }
          <span class="sd-sp"></span>
          @if (summary() && d.firstRowsMs !== undefined && d.firstRowsMs !== null) {
            <span class="sd-first" data-testid="search-supplier-summary-first">
              {{ 'First results' | translate }}: <b>{{ seconds(d.firstRowsMs) }} {{ 's' | translate }}</b>
            </span>
          }
          @if (d.totalMs !== undefined && d.totalMs !== null) {
            <span [class.sd-muted]="summary() && d.firstRowsMs !== undefined && d.firstRowsMs !== null">
              {{
                (summary() && d.firstRowsMs !== undefined && d.firstRowsMs !== null ? 'All suppliers' : 'Total')
                  | translate
              }}:
              @if (summary()) {
                <b data-testid="search-diagnostics-total-ms">{{ seconds(d.totalMs) }} {{ 's' | translate }}</b>
              } @else {
                <b data-testid="search-diagnostics-total-ms">{{ d.totalMs }} {{ 'ms' | translate }}</b>
              }
            </span>
          }
          <button
            type="button"
            class="sd-toggle"
            data-testid="search-diagnostics-toggle"
            [attr.aria-expanded]="open()"
            (click)="toggle()"
          >
            {{ (open() ? 'Hide' : 'Show') | translate }}
          </button>
        </header>
        @if (open()) {
          <div class="sd-table" role="table">
            <div class="sd-row sd-row-head" role="row">
              <span role="columnheader">{{ 'Provider' | translate }}</span>
              <span role="columnheader" class="sd-num">{{ 'Answer time' | translate }}</span>
              <span role="columnheader">{{ 'Outcome' | translate }}</span>
              <span role="columnheader" class="sd-num">{{ 'Offers found' | translate }}</span>
            </div>
            @for (row of rows(); track row.provider + row.kind) {
              <div
                class="sd-row"
                role="row"
                [class.sd-slowest]="summary() && row === slowest()"
                [attr.data-testid]="'search-diagnostics-row-' + id(row)"
              >
                <span role="cell" class="sd-provider">
                  <b>{{ row.provider }}</b>
                  @if (row.kind) {
                    <span class="sd-kind" [attr.data-testid]="'search-diagnostics-kind-' + id(row)">{{
                      row.kind | translate
                    }}</span>
                  }
                </span>
                @if (summary()) {
                  <span role="cell" class="sd-num" [attr.data-testid]="'search-diagnostics-ms-' + id(row)">
                    @if (answered(row) && row.ms !== null && row.ms !== undefined) {
                      {{ 'in {seconds} s {ms} ms' | translate: (row.ms | responseTimeParts) }}
                    } @else {
                      -
                    }
                    @if (row === slowest()) {
                      <span class="sd-slow-tag" data-testid="search-supplier-summary-slowest">{{
                        'Slowest' | translate
                      }}</span>
                    }
                  </span>
                } @else {
                  <span role="cell" class="sd-num" [attr.data-testid]="'search-diagnostics-ms-' + id(row)"
                    >{{ row.ms }} {{ 'ms' | translate }}</span
                  >
                }
                <span role="cell">
                  <span
                    [class]="'sd-badge sd-' + row.outcome"
                    [attr.data-testid]="'search-diagnostics-outcome-' + id(row)"
                    >{{ (summary() ? summaryLabel(row.outcome) : label(row.outcome)) | translate }}</span
                  >
                  @if (row.errorCode) {
                    <span class="sd-err" [attr.data-testid]="'search-diagnostics-error-' + id(row)">{{
                      row.errorCode
                    }}</span>
                  }
                </span>
                <span role="cell" class="sd-num" [attr.data-testid]="'search-diagnostics-results-' + id(row)">{{
                  row.results ?? '-'
                }}</span>
              </div>
            }
          </div>
          @for (group of droppedFlights(); track group.reason) {
            <div class="sd-dropped" [attr.data-testid]="'search-diagnostics-dropped-' + group.reason.toLowerCase()">
              <span>{{ droppedLabel(group.reason) | translate }}:</span>
              @for (drop of group.airports; track drop.airport) {
                <b [attr.data-testid]="'search-diagnostics-dropped-airport-' + drop.airport.toLowerCase()"
                  >{{ drop.airport }} {{ drop.flights }}</b
                >
              }
              <span class="sd-err">{{ group.reason }}</span>
            </div>
          }
        }
      </section>
    }
  `,
  styles: `
    :host {
      display: block;
      container-type: inline-size;
    }
    .sd-panel {
      border: 1px solid var(--kz-palette-light-blue-500);
      border-radius: 8px;
      background: var(--c-ground);
      margin-bottom: 0.75rem;
      font-size: 0.8125rem;
      color: var(--kz-color-text);
    }
    .sd-head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.375rem 0.875rem;
      padding: 0.625rem 0.75rem;
    }
    .sd-sp {
      flex: 1;
    }
    .sd-toggle {
      border: 1px solid var(--kz-palette-light-blue-500);
      background: var(--c-ground);
      border-radius: 6px;
      min-height: 2rem;
      padding: 0.125rem 0.75rem;
      color: var(--kz-color-text);
      cursor: pointer;
    }
    .sd-row {
      display: grid;
      grid-template-columns: 1.6fr 1fr 1.4fr 0.8fr;
      gap: 0.5rem;
      align-items: center;
      padding: 0.5rem 0.75rem;
      border-top: 1px solid var(--kz-pallete-mixed-light-blue);
    }
    .sd-row-head {
      color: var(--kz-color-text-secondary);
      font-weight: 600;
      font-size: 0.75rem;
    }
    .sd-num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .sd-kind,
    .sd-err {
      margin-left: 0.375rem;
      color: var(--kz-color-text-secondary);
      font-size: 0.75rem;
    }
    .sd-err {
      color: var(--kz-color-danger);
      display: block;
      margin-left: 0;
    }
    .sd-badge {
      display: inline-block;
      border-radius: 10px;
      padding: 0 0.625rem;
      font-weight: 600;
      white-space: nowrap;
    }
    .sd-ok {
      color: var(--kz-color-success);
      background: color-mix(in srgb, var(--kz-color-success) 14%, transparent);
    }
    .sd-empty {
      color: var(--kz-color-text-secondary);
      background: color-mix(in srgb, var(--kz-color-text-secondary) 14%, transparent);
    }
    .sd-partial {
      color: var(--kz-color-warning);
      background: color-mix(in srgb, var(--kz-color-warning) 18%, transparent);
    }
    .sd-skipped,
    .sd-not-needed {
      color: var(--kz-color-text-secondary);
      background: color-mix(in srgb, var(--kz-color-text-secondary) 8%, transparent);
      font-weight: 500;
    }
    .sd-dropped {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 0.25rem 0.75rem;
      padding: 0.5rem 0.75rem;
      border-top: 1px solid var(--kz-pallete-mixed-light-blue);
    }
    .sd-failed,
    .sd-timeout {
      color: var(--kz-color-danger);
      background: color-mix(in srgb, var(--kz-color-danger) 14%, transparent);
    }
    .sd-counts {
      color: var(--kz-color-text-secondary);
    }
    .sd-first b {
      font-size: 1.1em;
    }
    .sd-muted {
      opacity: 0.7;
    }
    .sd-slowest {
      background: color-mix(in srgb, var(--kz-color-warning) 12%, transparent);
      box-shadow: inset 3px 0 0 var(--kz-color-warning);
    }
    .sd-slow-tag {
      display: inline-block;
      margin-left: 0.375rem;
      border-radius: 10px;
      padding: 0 0.5rem;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--kz-color-warning);
      background: color-mix(in srgb, var(--kz-color-warning) 18%, transparent);
    }
    @container (max-width: 560px) {
      .sd-row-head {
        display: none;
      }
      .sd-row {
        grid-template-columns: 1fr 1fr;
      }
      .sd-row .sd-num {
        text-align: left;
      }
    }
  `
})
export class SearchDiagnosticsPanelComponent {
  /** The `diagnostics` block of the search answer; nothing is rendered without it. */
  readonly diagnostics = input<SearchDiagnostics | null | undefined>(null);

  /** diagnostics (Amiran's panel, website-web#47) or summary (the end-of-search supplier summary, run 24) */
  readonly mode = input<'diagnostics' | 'summary'>('diagnostics');

  readonly #websiteFlags = inject(WebsiteFlags);
  /** R-WF32: the block as sent, unless the agent's last flags read says the Supplier answers box is off */
  protected readonly shownDiagnostics = computed(() =>
    this.#websiteFlags.supplierAnswersHidden() ? null : this.diagnostics()
  );

  readonly open = signal(readOpen());

  protected readonly summary = computed(() => this.mode() === 'summary');
  protected readonly title = computed(() => (this.summary() ? 'Supplier answers' : 'Search diagnostics'));

  readonly rows = computed<ProviderDiagnostic[]>(() => {
    const rows = this.diagnostics()?.providers ?? [];
    if (!this.summary()) return rows;
    // answered first by time, then the ones that never answered
    return [...rows].sort((a, b) => Number(this.answered(b)) - Number(this.answered(a)) || (a.ms ?? 0) - (b.ms ?? 0));
  });

  readonly answeredCount = computed(
    () => this.rows().filter((r) => this.answered(r) || r.outcome === 'deferred').length
  );

  /** the slowest supplier that did answer - the one that held the search up */
  readonly slowest = computed<ProviderDiagnostic | undefined>(() => {
    const answered = this.rows().filter((r) => this.answered(r) && typeof r.ms === 'number');
    if (answered.length < 2) return undefined;
    return answered.reduce<ProviderDiagnostic | undefined>(
      (a, b) => (!a || (b.ms ?? 0) > (a.ms ?? 0) ? b : a),
      undefined
    );
  });

  /** R-NR1: the flights the search left out, one line per reason with its airports (largest first, as packages-api sends them). */
  readonly droppedFlights = computed(() => {
    const groups = new Map<string, DroppedFlightsDiagnostic[]>();
    for (const drop of this.diagnostics()?.droppedFlights ?? []) {
      if (!drop?.airport || !drop.reason) continue;
      groups.set(drop.reason, [...(groups.get(drop.reason) ?? []), drop]);
    }
    return [...groups].map(([reason, airports]) => ({ reason, airports }));
  });

  droppedLabel(reason: string): string {
    return reason === 'FLIGHTS_DROPPED_NO_DISTRICT'
      ? 'Flights left out - the airport has no district'
      : 'Flights left out';
  }

  answered(row: ProviderDiagnostic): boolean {
    return row.outcome === 'ok' || row.outcome === 'partial' || row.outcome === 'empty';
  }

  seconds(ms: number): string {
    return (Math.round(ms / 100) / 10).toFixed(1);
  }

  summaryLabel(outcome: string): string {
    switch (outcome) {
      case 'ok':
      case 'partial':
        return 'Answered';
      case 'empty':
        return 'No offers';
      case 'deferred':
        return 'Checked per row';
      case 'skipped':
        return 'Not asked';
      case 'not-needed':
        return 'Not needed';
      case 'timeout':
      case 'pending':
        return 'No answer';
      default:
        return 'Error';
    }
  }

  id(row: ProviderDiagnostic): string {
    return row.provider.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  label(outcome: string): string {
    if (outcome === 'skipped') return 'Not asked';
    if (outcome === 'not-needed') return 'Not needed';
    return outcome === 'ok' ? 'OK' : outcome.charAt(0).toUpperCase() + outcome.slice(1);
  }

  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    writeOpen(next);
  }
}
